import { ApolloError, useMutation, useQuery } from "@apollo/client";
import {
  BaseTable,
  Button,
  Flexbox,
  Icons,
  Sentiments,
  TextInput
} from "@sede-x/shell-ds-react-framework";
import { loader } from "graphql.macro";
import { useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import InlineLoadingPanel from "shared/components/InlineLoadingPanel";
import { GqlResponse, TMovement, TMovementComment } from "ticketing/ticketing.types";

const QUERY_MOVEMENT_COMMENTS = loader("../../ticketing-graphql/commentsOfMovement.graphql");
const UPDATE_MOVEMENT_COMMENTS = loader(
  "../../ticketing-graphql/updateCommentsOfMovements.graphql"
);

type Response = GqlResponse<Partial<TMovement>, "movement">;
type UpdateCommentsResponse = GqlResponse<Partial<TMovement>[], "movements">;
type MovementCommentsProps = {
  movement: TMovement;
  onClose: () => void;
};
type MovementCommentEdit = TMovementComment & {
  modifiedComment: string;
  inEdit?: boolean;
};

type CommentCellProps = {
  row: {
    original: MovementCommentEdit;
  };
  rowIndex: number;
  itemChange: (params: { rowIndex: number; field: string; value: string }) => void;
};

const CommentCell: React.FC<CommentCellProps> = ({ row, rowIndex, itemChange }) => {
  const isInEdit = row.original.inEdit === true;

  return isInEdit ? (
    <TextInput
      type="text"
      value={row.original.modifiedComment || ""}
      onChange={e =>
        itemChange({
          rowIndex,
          field: "modifiedComment",
          value: e.target.value
        })
      }
      style={{ width: "100%" }}
    />
  ) : (
    <span style={{ display: "block", width: "100%" }}>
      {row.original.modifiedComment || ""}
    </span>
  );
};

const columns = (
  itemChange: (params: { rowIndex: number; field: string; value: string }) => void
) => [
  {
    header: "Seq",
    accessorKey: "seqNum",
    editable: false
  },
  {
    header: "Comment",
    accessorKey: "modifiedComment",
    Cell: (props: { row: { original: MovementCommentEdit }; rowIndex: number }) => (
      <CommentCell row={props.row} rowIndex={props.rowIndex} itemChange={itemChange} />
    )
  }
];

export const MovementComments = ({ movement, onClose }: MovementCommentsProps) => {
  const [movementComments, setMovementComments] = useState<MovementCommentEdit[]>([]);

  const { loading, error } = useQuery<Response>(QUERY_MOVEMENT_COMMENTS, {
    fetchPolicy: "no-cache",
    variables: { id: movement.id },
    onCompleted: data =>
      setMovementComments(
        data.movement.comments
          ?.map(c => ({ ...c, modifiedComment: c.comment, inEdit: true }))
          .sort((a, b) => a.seqNum - b.seqNum) ?? []
      )
  });

  const [updateComments, { loading: uLoading, error: uError }] =
    useMutation<UpdateCommentsResponse>(UPDATE_MOVEMENT_COMMENTS, {
      onCompleted: data => {
        setMovementComments(
          data.movements
            .filter(m => m.id === movement.id)
            .flatMap(m => m.comments ?? [])
            .map(c => ({ ...c, modifiedComment: c.comment, inEdit: true }))
            .sort((a, b) => a.seqNum - b.seqNum)
        );
      }
    });

  const hasModifiedComments = movementComments.some(c => c.comment !== c.modifiedComment);

  const onSave = () => {
    if (hasModifiedComments) {
      updateComments({
        variables: {
          comments: [
            {
              id: movement.id,
              version: movement.version,
              comments: movementComments
                .filter(c => c.comment !== c.modifiedComment)
                .map(({ seqNum, modifiedComment }) => ({
                  seqNum,
                  comment: modifiedComment
                }))
            }
          ]
        }
      });
    }
  };

  const itemChange = ({
    rowIndex,
    field,
    value
  }: {
    rowIndex: number;
    field: string;
    value: string;
  }) => {
    const newData = movementComments.map((item, index) =>
      index === rowIndex ? { ...item, [field]: value } : item
    );
    setMovementComments(newData); // Update the state with the modified data
  };

  const saveDisabled = loading || uLoading || !hasModifiedComments;

  if (error || uError) {
    return <ApolloErrorViewer error={[error, uError].filter((e): e is ApolloError => !!e)} />;
  }

  return (
    <div className={"content-wrapper"}>
      <div className={"grid-wrapper"}>
        <div className={"card-container"}>
          <div style={{ maxHeight: "296px", overflowY: "auto", margin: "2px" }}>
            {(loading || uLoading) && <InlineLoadingPanel />}
            <Flexbox justifyContent="space-between">
              <Button
                icon={<Icons.SaveAlt />}
                onClick={onSave}
                disabled={saveDisabled}
                size="small"
                sentiment={Sentiments.Positive}>
                Save
              </Button>
              <Button
                icon={<Icons.Cross />}
                size="small"
                onClick={onClose}
                sentiment={Sentiments.Negative}
                disabled={loading || uLoading}>
                Close
              </Button>
            </Flexbox>
            <BaseTable
              data={movementComments}
              columns={columns(itemChange)}
              style={{
                width: "100%",
                height: "100%"
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
